/**
 * Toast notification state management using TC39 Signals.
 */
import { Signal } from 'signal-polyfill';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  supportId?: string;
  duration?: number;
}

export interface ToastStateShape {
  toasts: Toast[];
}

const initialToastState: ToastStateShape = {
  toasts: [],
};

/**
 * Reactive toast state signal.
 */
export const toastState = new Signal.State<ToastStateShape>(initialToastState);

/**
 * Computed signal: are there any toasts
 */
export const hasToasts = new Signal.Computed(() => {
  return toastState.get().toasts.length > 0;
});

let toastIdCounter = 0;
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function clearToastTimeout(id: string): void {
  const timeoutId = toastTimeouts.get(id);
  if (timeoutId === undefined) {
    return;
  }

  clearTimeout(timeoutId);
  toastTimeouts.delete(id);
}

function clearAllToastTimeouts(): void {
  for (const timeoutId of toastTimeouts.values()) {
    clearTimeout(timeoutId);
  }

  toastTimeouts.clear();
}

/**
 * Toast management utilities
 */
export const toastActions = {
  /**
   * Add a new toast notification.
   */
  addToast(toast: Omit<Toast, 'id'>): string {
    const id = `toast-${++toastIdCounter}`;
    const newToast: Toast = {
      id,
      duration: 5000, // Default 5 seconds
      ...toast,
    };

    const current = toastState.get();
    toastState.set({
      toasts: [...current.toasts, newToast],
    });

    // Auto-remove after duration
    if (newToast.duration && newToast.duration > 0) {
      const timeoutId = setTimeout(() => {
        toastTimeouts.delete(id);
        toastActions.removeToast(id);
      }, newToast.duration);

      toastTimeouts.set(id, timeoutId);
    }

    return id;
  },

  /**
   * Remove a toast by ID.
   */
  removeToast(id: string): void {
    clearToastTimeout(id);

    const current = toastState.get();
    toastState.set({
      toasts: current.toasts.filter((t) => t.id !== id),
    });
  },

  /**
   * Clear all toasts.
   */
  clearAll(): void {
    clearAllToastTimeouts();
    toastState.set({ toasts: [] });
  },

  /**
   * Convenience method: show success toast.
   */
  success(message: string, supportId?: string): string {
    return toastActions.addToast({ type: 'success', message, supportId });
  },

  /**
   * Convenience method: show error toast.
   */
  error(message: string, supportId?: string): string {
    return toastActions.addToast({ type: 'error', message, supportId, duration: 8000 });
  },

  /**
   * Convenience method: show info toast.
   */
  info(message: string, supportId?: string): string {
    return toastActions.addToast({ type: 'info', message, supportId });
  },

  /**
   * Convenience method: show warning toast.
   */
  warning(message: string, supportId?: string): string {
    return toastActions.addToast({ type: 'warning', message, supportId, duration: 6000 });
  },
};
